import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import jwt from 'jsonwebtoken'


export function getStorage(): Storage {
    try {
        return localStorage || sessionStorage

    } catch (error) {
        return null
    }
}

const storage: Storage = getStorage()

export interface DecodedAccessToken {
    iat: number
    exp: number
    roles: string[]
    email: string
    id: string
    companyId: string
}

export class AuthClient {
    protected readonly client: AxiosInstance
    protected basePath!: string
    protected retryAttempts: number = 0

    constructor() {
        this.basePath = process.env.VUE_APP_KEYVUE_BACKEND_BASEPATH || ''
        this.client = axios.create({
            baseURL: process.env.VUE_APP_KEYVUE_BACKEND_BASEURL,
            headers: {
                'Content-Type': 'application/ld+json',
            },
        })

        this.client.interceptors.request.use((request: AxiosRequestConfig) => this.onRequest(request))
        this.client.interceptors.response.use(undefined, (error: AxiosError) => this.onError(error))
    }

    private onRequest(request: AxiosRequestConfig): AxiosRequestConfig {
        if (!!this.accessToken) {
            request.headers.Authorization = `Bearer ${this.accessToken}`
        }

        return request
    }

    private async onError(error: AxiosError): Promise<any> {
        if (error.response === undefined) {
            console.error(error)
            throw Error('Er kon geen verbinding gemaakt worden met de server. Probeer het later nogmaals of neem contact met ons op: marketing@winck.nl')
        }

        if (error.response.status === 401) {
            if (this.retryAttempts >= 10) {
                await this.logout()
                return
            }

            this.retryAttempts++
            if (!!this.refreshToken) {
                await this.doRefreshToken()

                // Retry original request
                const config = error.config
                error.config.headers.Authorization = `Bearer ${this.accessToken}`
                return Promise.resolve(this.client(config))
            }

            await this.logout()
        }

        return Promise.reject(error)
    }

    private async doRefreshToken() {
        try {
            const response = await this.client.post('/refresh_token', {
                refresh_token: this.refreshToken,
            })

            if (response.status === 200) {
                this.accessToken = response.data.token
                this.refreshToken = response.data.refresh_token
            }

        } catch (error) {
            await this.logout()
            throw error
        }
    }

    async login(email: string, password: string): Promise<void> {
        const response = await this.client.post('/authentication_token', {
            email,
            password,
        })

        if (storage) {
            storage.setItem('access_token', response.data.token)
            storage.setItem('refresh_token', response.data.refresh_token)
            document.cookie = `access_token=${response.data.token}`
            document.cookie = `refresh_token=${response.data.refresh_token}`
        }
    }

    async logout(): Promise<void> {
        if (storage) {
            storage.removeItem('access_token')
            storage.removeItem('refresh_token')
        }
    }

    get isAuthenticated(): boolean {
        return !!this.accessToken
    }

    get accessToken(): string {
        if (storage) {
            return storage.getItem('access_token') || null
        }
    }

    set accessToken(value: string) {
        if (storage) {
            storage.setItem('access_token', value)
        }
    }

    get refreshToken(): string {
        if (storage) {
            return storage.getItem('refresh_token') || null
        }
    }

    set refreshToken(value: string) {
        if (storage) {
            storage.setItem('refresh_token', value)
        }
    }

    get decodedAccessToken(): DecodedAccessToken {
        return jwt.decode(this.accessToken) as DecodedAccessToken
    }

    get axiosClient(): AxiosInstance {
        return this.client
    }
}
